import { useState } from 'react';
import { Button, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { Link, useParams } from 'react-router';
import {
  SAHA_TURU_DURUM_ETIKETI,
  SAHA_TURU_TIP_ETIKETI,
  TATBIKAT_TURU_COKLU_ETIKETI,
  TATBIKAT_TURU_ETIKETI,
  sahaTuruAlaniVarMi,
} from '@isg/validation';
import type { SahaTuruDetay } from '@isg/validation';
import { useCiktiIndir, useSahaTuru } from '../api/queries';
import { DofSayaci } from './olay-dof-sayaci';
import { QueryStatus } from './parts';
import { EkBolumu, EkipBolumu, GozlemBolumu, KapanisBolumu } from './saha-turu-parts';
import { KapIcindeFormDoldurma } from './FormDoldurScreen';
import { GundemineEkleDugmesi } from './kurul-parts';
import { Bolum, KapiSebebi, KayitDetayi, Rozet } from '../sozlesme';
import { tarihGoster, zamanGoster } from '../lib/format';

/**
 * SAHA TURU DETAYI (Kapsam §27; sözleşme P5 · P9 · P11).
 *
 * Bölümler alt alta (P5 — PDF sayfanın kendisidir): künye (P9 ızgarası) ·
 * ekip · denetim formu · gözlemler · kapanış. ⚠️ **KAPANIŞ DÜĞMESİNİN
 * SEBEBİ YANINDA** (G5): pasifse `yetkiler.kapanisEngeli` sunucudan gelir —
 * *"Önce denetim formunu gönderin"* gibi; ekran koşul türetmez (§2.40).
 * ⚠️ Kapalı turda hiçbir yazma denetimi çizilmez; tur yeniden açılmaz (S9).
 * KPI sayacı DÖF sayacıyla aynı bileşen (S9).
 */
export function SahaTuruDetayScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const tur = useSahaTuru(id);
  const indir = useCiktiIndir();
  const [kapanisAcik, setKapanisAcik] = useState(false);

  if (!tur.isSuccess) return <QueryStatus queries={[tur]} />;
  const t = tur.data;
  return (
    <KayitDetayi
      testId="saha-turu-detay"
      geriYol="/saha-turlari"
      geriEtiket="Saha turları"
      baslik={t.takipNo}
      ustBilgi={
        <Group gap="xs">
          <Rozet anlam="bilgi" testId="saha-turu-tip-rozeti">
            {SAHA_TURU_TIP_ETIKETI[t.tip]}
          </Rozet>
          <Rozet anlam={t.durum === 'acik' ? 'dikkat' : 'bilgi'} testId="saha-turu-durum">
            {SAHA_TURU_DURUM_ETIKETI[t.durum]}
          </Rozet>
          <DofSayaci
            bosCizilmez
            toplam={t.bulguOzeti.toplam}
            acik={t.bulguOzeti.acik}
            testId="saha-turu-bulgu-sayaci"
          />
          {t.olumluSayisi > 0 && (
            <Rozet anlam="olumlu" testId="saha-turu-olumlu-sayisi">
              {t.olumluSayisi} olumlu gözlem
            </Rozet>
          )}
        </Group>
      }
      eylemler={
        <Group gap="xs" align="center">
          {/* İSG kurulu (§28/C6): görünürlük + engel sunucudan — kurul yoksa görünmez. */}
          <GundemineEkleDugmesi
            workplaceId={t.workplaceId}
            baslik={`Saha turu ${t.takipNo} tutanağı`}
            kaynak={{ siteTourId: t.id }}
            testId="saha-turu-gundeme-ekle"
          />
          <Button
            variant="default"
            data-testid="saha-turu-pdf"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({ yol: `/api/site-tours/${t.id}/pdf`, yedekAd: `${t.takipNo}.pdf` })
            }
          >
            Tutanak PDF
          </Button>
          {t.durum === 'acik' && (
            <>
              {t.yetkiler.kapanisEngeli !== null && t.yetkiler.yazabilir && (
                <Text size="xs" c="dimmed" data-testid="saha-turu-kapanis-engeli">
                  {t.yetkiler.kapanisEngeli}
                </Text>
              )}
              {(t.yetkiler.kapatabilir || t.yetkiler.yazabilir) && (
                <Button
                  data-testid="saha-turu-kapat"
                  disabled={!t.yetkiler.kapatabilir}
                  onClick={() => setKapanisAcik((a) => !a)}
                >
                  {kapanisAcik ? 'Kapanışı iptal' : 'Turu kapat'}
                </Button>
              )}
            </>
          )}
        </Group>
      }
    >
      <Kunye t={t} />
      {kapanisAcik && t.durum === 'acik' && (
        <KapanisBolumu
          tur={t}
          onKapandi={() => setKapanisAcik(false)}
          onVazgec={() => setKapanisAcik(false)}
        />
      )}
      <EkipBolumu tur={t} />
      {t.tip === 'denetim' && <DenetimFormuBolumu t={t} />}
      <GozlemBolumu tur={t} />
      {/* E5 — dış rapor tutanak PDF ile YAN YANA (kapalı turda da yüklenir). */}
      <EkBolumu tur={t} />
    </KayitDetayi>
  );
}

/** P9 — kısa alanlar ızgarada. */
function Kunye({ t }: { t: SahaTuruDetay }): React.JSX.Element {
  return (
    <Bolum baslik="Künye" testId="saha-turu-kunye">
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
        <KunyeAlani etiket="Tarih" deger={tarihGoster(t.tarih)} />
        <KunyeAlani etiket="İşyeri" deger={`${t.companyAd} — ${t.workplaceAd}`} />
        <KunyeAlani etiket="Lokasyon" deger={t.locationAd ?? '—'} />
        {sahaTuruAlaniVarMi(t.tip, 'birim') && (
          <KunyeAlani
            etiket="Denetlenen birim"
            deger={t.birimAd ?? '—'}
            testId="saha-turu-kunye-birim"
          />
        )}
        <KunyeAlani etiket="Lider" deger={t.liderEtiket} testId="saha-turu-kunye-lider" />
        {sahaTuruAlaniVarMi(t.tip, 'sablon') && (
          <KunyeAlani etiket="Şablon" deger={t.formTemplateAd ?? '—'} />
        )}
        {t.tatbikat && (
          <>
            {/* G4.1 — ÇOKLU tür; şerh etikette (tek kaynak). */}
            <KunyeAlani
              etiket={TATBIKAT_TURU_COKLU_ETIKETI}
              deger={t.tatbikat.turler.map((tur) => TATBIKAT_TURU_ETIKETI[tur]).join(' · ')}
              testId="saha-turu-kunye-tatbikat-turu"
            />
            <KunyeAlani
              etiket="Süre"
              deger={t.tatbikat.sureDakika === null ? '—' : `${t.tatbikat.sureDakika} dk`}
              testId="saha-turu-kunye-tatbikat-sure"
            />
            <KunyeAlani
              etiket="Tahliye edilen kişi"
              deger={
                t.tatbikat.tahliyeKisiSayisi === null ? '—' : String(t.tatbikat.tahliyeKisiSayisi)
              }
              testId="saha-turu-kunye-tatbikat-tahliye"
            />
            <KunyeAlani
              etiket="Katılan ekipler"
              deger={
                t.tatbikat.ekipler.length ? t.tatbikat.ekipler.map((e) => e.ad).join(' · ') : '—'
              }
              testId="saha-turu-kunye-tatbikat-ekipler"
            />
            <KunyeAlani
              etiket="Senaryo"
              deger={t.tatbikat.senaryo}
              testId="saha-turu-kunye-tatbikat-senaryo"
            />
          </>
        )}
        <KunyeAlani
          etiket="Kapanış"
          deger={t.kapanisAt ? zamanGoster(t.kapanisAt) : '—'}
          testId="saha-turu-kunye-kapanis"
        />
      </SimpleGrid>
    </Bolum>
  );
}

function KunyeAlani({
  etiket,
  deger,
  testId,
}: {
  etiket: string;
  deger: React.ReactNode;
  testId?: string;
}): React.JSX.Element {
  return (
    <Stack gap={2} data-testid={testId}>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      {typeof deger === 'string' ? <Text size="sm">{deger}</Text> : deger}
    </Stack>
  );
}

/**
 * Denetim formu (S1/S2): kap içindeki TEK form kaydı. ⚠️ **FORM TURUN
 * İÇİNDE DOLDURULUR** (gezinti artçısı 07.09.2026 — G2.1): eskiden düğme
 * kullanıcıyı Formlar modülüne atıyordu ve kap kavramı ekranda kırılıyordu
 * (kural doğru çalışıyordu — gönderim tura bağlanıyordu — sorun YÜZEYDİ).
 * Gövde form motorunun kendi bileşenidir (`KapIcindeFormDoldurma`); taslak
 * varsa aynı gövde onu yükler. Gönderildiyse kayıt bağlantısı + ✗ sayısı —
 * bulgu seçimi KAPANIŞTA yapılır (S4), burada değil.
 *
 * ✏️ **T4 (türetme paketi — madde 265, 17.09.2026): AYRI "Formu doldur" ADIMI
 * YOK.** Taslak tur açılışında sunucuda doğar; gövde doğrudan görünür (eski
 * turda taslak yoksa gövde ilk açılışta açar). Kapı TURUN kapısıdır
 * (`formKapisi` — sunucu); başlık bloğu gizli, üst kaydın bildiği sorulmaz.
 */
function DenetimFormuBolumu({ t }: { t: SahaTuruDetay }): React.JSX.Element {
  const form = t.denetimFormu;
  const gonderilmis = form !== null && form.durum === 'submitted';
  // Doldurma: açık tur ∧ yetki (sunucudan) ∧ gönderilmiş form yok.
  const doldurulabilir = !gonderilmis && t.yetkiler.formDoldurabilir;
  return (
    <Bolum baslik="Denetim formu" testId="saha-turu-denetim-formu">
      <Stack gap="sm">
        <Group gap="md">
          {form === null ? (
            <Text size="sm" c="dimmed">
              Form henüz doldurulmadı.
            </Text>
          ) : (
            <Rozet anlam={gonderilmis ? 'olumlu' : 'dikkat'} testId="saha-turu-form-durumu">
              {gonderilmis ? 'Gönderildi' : 'Taslak'}
            </Rozet>
          )}
          {gonderilmis && form && (
            <>
              <Link
                to={`/formlar/kayitlar/${form.formSubmissionId}`}
                data-testid="saha-turu-form-kaydi"
              >
                Form kaydına git
              </Link>
              <Text size="sm" data-testid="saha-turu-form-uygunsuz-sayisi">
                {form.uygunsuzMaddeler.length} uygunsuz madde
              </Text>
            </>
          )}
        </Group>
        {/* ✏️ T4 — kapı TURUN kapısıdır (şapka → bahçe → arşiv → tur açık). */}
        {!gonderilmis && t.durum === 'acik' && (
          <KapiSebebi
            durum={t.yetkiler.formKapisi}
            nesne="Denetim formunu"
            fiil="doldurur"
            testId="saha-turu-form-yazamama-sebebi"
          />
        )}
        {doldurulabilir && t.formTemplateId && (
          <Card withBorder padding="sm" data-testid="saha-turu-denetim-formu-govde">
            <KapIcindeFormDoldurma
              sablonId={t.formTemplateId}
              sahaTuruId={t.id}
              mevcutKayitId={form?.formSubmissionId ?? null}
              onGonderildi={() => undefined}
            />
          </Card>
        )}
      </Stack>
    </Bolum>
  );
}
